import React, { useCallback, useContext, useState } from 'react';
import { Avatar, Button, notification, Input } from 'antd';
import { HeartOutlined, HeartFilled, MessageOutlined, SendOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';

import { AppContext } from 'context/AppContext';
import { Comment } from 'services/types/CommentTypes';
import { formatRelativeTime } from 'utils/DateUtils';
import './Comment.css';

const { TextArea } = Input;

interface CommentItemProps {
  comment: Comment;
  onLikeUpdate?: (updatedComment: Comment) => void;
  onCommentAdd?: (newComment: Comment) => void;
  isChild?: boolean;
}

// Reply Input Component Props
interface ReplyInputProps {
  visible: boolean;
  onSubmit: (message: string) => Promise<void>;
  onCancel: () => void;
  placeholder: string;
  isSubmitting?: boolean;
}

const ReplyInput: React.FC<ReplyInputProps> = ({
  visible,
  onSubmit,
  onCancel,
  placeholder,
  isSubmitting = false
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');

  const handleSubmit = useCallback(async () => {
    if (!value.trim()) return;

    try {
      await onSubmit(value.trim());
      setValue('');
    } catch (error) {
      // Error handling is done in parent component
    }
  }, [value, onSubmit]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  if (!visible) return null;

  return (
    <div className="reply-input-container">
      <div className="reply-input-wrapper">
        <TextArea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={placeholder}
          autoSize={{ minRows: 1, maxRows: 3 }}
          onKeyPress={handleKeyPress}
          disabled={isSubmitting}
          className="reply-input"
          autoFocus
        />
        <div className="reply-input-actions">
          <Button
            type="text"
            size="small"
            onClick={onCancel}
            disabled={isSubmitting}
            className="reply-cancel-btn"
          >
            {t('#Comment.cancel')}
          </Button>
          <Button
            type="primary"
            size="small"
            onClick={handleSubmit}
            disabled={!value.trim() || isSubmitting}
            loading={isSubmitting}
            icon={!isSubmitting && <SendOutlined />}
            className="reply-send-btn"
          >
            {t('#Comment.reply')}
          </Button>
        </div>
      </div>
    </div>
  );
};

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  onLikeUpdate,
  onCommentAdd,
  isChild = false,
}) => {
  const { t } = useTranslation();
  const { api, authentication } = useContext(AppContext);
  const { authKey } = authentication;
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [isSubmittingReply, setIsSubmittingReply] = useState(false);
  const navigate = useNavigate();

  const handleLikeToggle = useCallback(async () => {
    if (!authKey) {
      notification.warning({
        message: t('#Comment.auth_required'),
        description: t('#Comment.login_to_like'),
      });
      return;
    }

    // Update UI immediately for instant feedback
    const updatedComment = {
      ...comment,
      is_liked: comment.is_liked === 1 ? 0 : 1, // Fixed: Use is_liked instead of isLiked
      like_count: comment.is_liked === 1 ? comment.like_count - 1 : comment.like_count + 1
    };

    if (onLikeUpdate) {
      onLikeUpdate(updatedComment);
    }

    // Make API call in background
    try {
      let result;
      if (comment.is_liked === 1) {
        result = await api.comment.unlikeComment(comment.id);
      } else {
        result = await api.comment.likeComment(comment.id);
      }

      if (!result.success) {
        // Revert changes if API call failed
        if (onLikeUpdate) {
          onLikeUpdate(comment);
        }
        notification.error({
          message: t(''),
          description: t('#Comment.error.like_error'),
        });
      }
    } catch (error) {
      // Revert changes if API call failed
      if (onLikeUpdate) {
        onLikeUpdate(comment);
      }
      notification.error({
        message: t(''),
        description: t('#Comment.error.like_error'),
      });
    }
  }, [comment, authKey, api.comment, onLikeUpdate, t]);

  const handleCommentClick = useCallback(() => {
    if (!authKey) {
      notification.warning({
        message: t('#Comment.auth_required'),
        description: t('#Comment.login_to_reply'),
      });
      return;
    }

    setShowReplyInput(!showReplyInput);
  }, [authKey, showReplyInput, t]);

  const handleReplySubmit = useCallback(async (message: string) => {
    setIsSubmittingReply(true);

    try {
      const commentURL = `${process.env.REACT_APP_DOMAIN}/api/comments/${comment.id}/`;
      const result = await api.comment.submitComment({
        object: commentURL,
        message: message,
        parentCommentId: comment.id
      });

      if (result.success) {
        notification.success({
          message: t(''),
          description: t('#Comment.reply_added_successfully'),
        });

        setShowReplyInput(false);

        // Notify parent component about new reply
        if (onCommentAdd && result.data) {
          onCommentAdd(result.data);
        }
      } else {
        notification.error({
          message: t(''),
          description: t('#Comment.error.submit_error'),
        });
      }
    } catch (error) {
      notification.error({
        message: t(''),
        description: t('#Comment.error.submit_error'),
      });
    } finally {
      setIsSubmittingReply(false);
    }
  }, [comment.id, api.comment, onCommentAdd, t]);

  const handleReplyCancel = useCallback(() => {
    setShowReplyInput(false);
  }, []);

  const handleUserClick = useCallback(() => {
    if (comment.user?.id) {
      navigate(`/profile/${comment.user.id}`);
    }
  }, [navigate, comment.user?.id]);

  return (
    <div className={`comment-item ${isChild ? 'comment-item-child' : 'comment-item-parent'}`}>
      {/* Comment Header */}
      <div className="comment-item-header">
        <div className="comment-item-user" style={{ cursor: 'pointer' }} onClick={handleUserClick}>
          <Avatar
            size={isChild ? 32 : 40}
            src={comment.user?.profile?.image_small_url}
            className="comment-item-avatar"
          />
          <div className="comment-item-user-info">
            <h5 className="comment-item-user-name">
              {`${comment.user?.first_name} ${comment.user?.last_name}`}
            </h5>
            <span className="comment-item-time">
              {formatRelativeTime(comment.created, t)}
            </span>
          </div>
        </div>
      </div>

      {/* Comment Content */}
      <div className="comment-item-content">
        <div className="comment-item-message">
          {comment.message}
        </div>

        {/* Comment Actions */}
        <div className="comment-item-actions">
          <div className="comment-item-actions-left">
            <Button
              type="text"
              size="small"
              className={`comment-like-btn ${comment.is_liked === 1 ? 'liked' : ''}`}
              onClick={handleLikeToggle}
              icon={comment.is_liked === 1 ? (
                <HeartFilled style={{ fontSize: '16px' }} />
              ) : (
                <HeartOutlined style={{ fontSize: '16px' }} />
              )}
              title={comment.is_liked === 1 ? t('#Comment.unlike') : t('#Comment.like')}
            />

            {/* Only show comment button for parent comments */}
            {!isChild && (
              <Button
                type="text"
                size="small"
                className={`comment-reply-btn ${showReplyInput ? 'active' : ''}`}
                onClick={handleCommentClick}
                icon={<MessageOutlined style={{ fontSize: '14px' }} />}
                title={t('#Comment.reply')}
              >
                {showReplyInput ? t('#Comment.cancel') : t('#Comment.reply')}
              </Button>
            )}
          </div>

          <div className="comment-item-stats">
            {comment.like_count > 0 && (
              <span className="comment-stat">
                {comment.like_count} {t('#Comment.like', { count: comment.like_count })}
              </span>
            )}
          </div>
        </div>

        {/* Reply Input - Only for parent comments */}
        {!isChild && (
          <ReplyInput
            visible={showReplyInput}
            onSubmit={handleReplySubmit}
            onCancel={handleReplyCancel}
            placeholder={t('#Comment.write_reply')}
            isSubmitting={isSubmittingReply}
          />
        )}
      </div>

      {/* Child Comments */}
      {!isEmpty(comment.child_comments) && (
        <div className="comment-item-children">
          <div className="comment-children-header">
            <span className="comment-children-count">
              {comment.child_comments.length} {t('#Comment.reply', { count: comment.child_comments.length })}
            </span>
          </div>
          <div className="comment-children-list">
            {comment.child_comments.map((childComment) => (
              <CommentItem
                key={childComment.id}
                comment={childComment}
                onLikeUpdate={onLikeUpdate}
                onCommentAdd={onCommentAdd}
                isChild={true}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentItem;